import { useState, useEffect, useMemo, useRef } from "react";
import { Sv, ic } from "../../components/common/Icons";
import ITAModal from "./ITAModal";
import api from "../../services/api";
import "../../assets/styles/ITAdmin/ITAPeoplePg.css";

/* ── CSV download helper ─────────────────────────────────────── */
function downloadCSV(rows, filename) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const headers = ["Emp ID","Name","Department","Role","Type","Status","Joined","Assets","Credentials"];
  const lines = [
    headers.join(","),
    ...rows.map((p) =>
      [p.id, p.name, p.dept||"", p.role||"", p.type||"",
       p.status||"", p.joined||"", p._assetCount??0,
       p._hasCred ? "Yes" : "No"
      ].map(esc).join(",")
    ),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

/* ── Profile CSV download ────────────────────────────────────── */
function downloadProfileCSV(sel, pAssets, cred) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [];
  lines.push(esc("IT Profile Report"));
  lines.push(esc("Generated: " + new Date().toLocaleString("en-IN")));
  lines.push("");
  lines.push(["Field","Value"].map(esc).join(","));
  lines.push(["Name",             sel.name        ].map(esc).join(","));
  lines.push(["Employee ID",      sel.type        ].map(esc).join(","));
  lines.push(["Department",       sel.dept||"—"   ].map(esc).join(","));
  lines.push(["Role",             sel.role||"—"   ].map(esc).join(","));
  lines.push(["Status",           sel.status||"—" ].map(esc).join(","));
  lines.push(["Joined",           sel.joined||"—" ].map(esc).join(","));
  lines.push("");
  lines.push([esc("ASSIGNED ASSETS"),esc("")].join(","));
  lines.push(["Asset Name","Asset ID","Type","Status"].map(esc).join(","));
  if (pAssets.length === 0) {
    lines.push(["No assets assigned","","",""].map(esc).join(","));
  } else {
    pAssets.forEach(a => lines.push([a.brand, a.serial, a.assetType, a.status].map(esc).join(",")));
  }
  lines.push("");
  lines.push([esc("CREDENTIALS & ACCESS"),esc("")].join(","));
  if (!cred) {
    lines.push(["No credentials provisioned",""].map(esc).join(","));
  } else {
    lines.push(["Username",  cred.lapUser||"—"         ].map(esc).join(","));
    lines.push(["IP Address",cred.ip||"—"              ].map(esc).join(","));
    lines.push(["Email",     cred.email||"—"           ].map(esc).join(","));
    lines.push(["VPN",       cred.vpn?"Enabled":"Disabled"].map(esc).join(","));
    if ((cred.software||[]).length>0) lines.push(["Software",(cred.software||[]).join(", ")].map(esc).join(","));
    if ((cred.ai||[]).length>0)       lines.push(["AI Tools",(cred.ai||[]).join(", ")      ].map(esc).join(","));
    if ((cred.sysAccess||[]).length>0)lines.push(["System Access",(cred.sysAccess||[]).join(", ")].map(esc).join(","));
  }
  const blob = new Blob(["\uFEFF"+lines.join("\n")], { type:"text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const safe = (sel.name||"profile").replace(/[^a-zA-Z0-9]/g,"_");
  a.href=url; a.download=`IT_Profile_${safe}.csv`; a.click();
  URL.revokeObjectURL(url);
}

/* ── Profile PDF download (print-based, same tab) ───────────── */
function downloadProfilePDF(sel, pAssets, cred) {
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
  <title>IT Profile — ${sel.name}</title>
  <style>
    body{font-family:Arial,sans-serif;margin:32px;color:#1e293b;font-size:13px;}
    h1{font-size:20px;color:#1d4ed8;margin-bottom:4px;}
    .sub{color:#64748b;font-size:12px;margin-bottom:20px;}
    .badge{display:inline-block;background:#eff6ff;color:#1d4ed8;border:1px solid #bfdbfe;border-radius:6px;padding:2px 10px;font-size:11px;font-weight:700;margin-right:6px;}
    .sec{font-size:13px;font-weight:700;color:#4f46e5;border-bottom:2px solid #e0e7ff;padding-bottom:4px;margin:18px 0 10px;}
    table{width:100%;border-collapse:collapse;margin-bottom:12px;}
    td{padding:7px 10px;border:1px solid #e2e8f0;font-size:12px;}
    td:first-child{font-weight:600;background:#f8fafc;width:140px;color:#475569;}
    .tag{display:inline-block;background:#f1f5f9;border:1px solid #e2e8f0;border-radius:4px;padding:2px 8px;margin:2px;font-size:11px;}
    .footer{margin-top:32px;font-size:10px;color:#94a3b8;border-top:1px solid #e2e8f0;padding-top:8px;}
    @media print{body{margin:16px;}}
  </style></head><body>
  <h1>IT Profile — ${sel.name}</h1>
  <div class="sub">
    <span class="badge">${sel.type||""}</span>
    <span class="badge">${sel.status||""}</span>
    Generated: ${new Date().toLocaleString("en-IN")}
  </div>
  <div class="sec">Basic Information</div>
  <table>
    <tr><td>Name</td><td>${sel.name||"—"}</td></tr>
    <tr><td>Department</td><td>${sel.dept||"—"}</td></tr>
    <tr><td>Role</td><td>${sel.role||"—"}</td></tr>
    <tr><td>Status</td><td>${sel.status||"—"}</td></tr>
    <tr><td>Joined</td><td>${sel.joined||"—"}</td></tr>
  </table>
  <div class="sec">Assigned Assets (${pAssets.length})</div>
  ${pAssets.length===0
    ? "<p style='color:#94a3b8'>No assets assigned</p>"
    : `<table><tr><td><b>Name</b></td><td><b>Asset ID</b></td><td><b>Type</b></td><td><b>Status</b></td></tr>
       ${pAssets.map(a=>`<tr><td>${a.brand}</td><td>${a.serial}</td><td>${a.assetType}</td><td>${a.status}</td></tr>`).join("")}
       </table>`
  }
  <div class="sec">Credentials & Access</div>
  ${!cred
    ? "<p style='color:#94a3b8'>No credentials provisioned</p>"
    : `<table>
       <tr><td>Username</td><td>${cred.lapUser||"—"}</td></tr>
       <tr><td>IP Address</td><td>${cred.ip||"—"}</td></tr>
       <tr><td>Email</td><td>${cred.email||"—"}</td></tr>
       <tr><td>VPN</td><td>${cred.vpn?"Enabled":"Disabled"}</td></tr>
       ${(cred.software||[]).length>0?`<tr><td>Software</td><td>${cred.software.map(s=>`<span class='tag'>${s}</span>`).join("")}</td></tr>`:""}
       ${(cred.ai||[]).length>0?`<tr><td>AI Tools</td><td>${cred.ai.map(a=>`<span class='tag'>${a}</span>`).join("")}</td></tr>`:""}
       ${(cred.sysAccess||[]).length>0?`<tr><td>System Access</td><td>${cred.sysAccess.map(s=>`<span class='tag'>${s}</span>`).join("")}</td></tr>`:""}
       </table>`
  }
  <div class="footer">AMSHRMS — IT Admin Portal &nbsp;·&nbsp; ${sel.name} IT Profile</div>
  <script>window.onload=()=>{window.print();}</script>
  </body></html>`;

    const blob = new Blob([html], { type:"text/html;charset=utf-8" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    const safe = (sel.name||"profile").replace(/[^a-zA-Z0-9]/g,"_");
    a.href = url; a.download = `IT_Profile_${safe}.pdf.html`; a.click();
    URL.revokeObjectURL(url);
    }

/* ── Employee Autocomplete Dropdown ─────────────────────────── */
function EmpDropdown({ people, selectedId, onChange }) {
  const [query,    setQuery]    = useState("");
  const [open,     setOpen]     = useState(false);
  const [hovered,  setHovered]  = useState(null);
  const wrapRef = useRef(null);

  const selected = people.find((p) => p.id === selectedId) || null;

  /* close on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const displayText = selected ? `${selected.name}  (${selected.id})` : query;

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return people.slice(0, 50);           // show first 50 when empty
    return people.filter((p) =>
      (p.name || "").toLowerCase().includes(q) ||
      (p.id   || "").toLowerCase().includes(q) ||
      (p.dept || "").toLowerCase().includes(q) ||
      (p.role || "").toLowerCase().includes(q)
    ).slice(0, 50);
  }, [people, query]);

  const select = (p) => {
    setQuery("");
    setOpen(false);
    onChange(p ? p.id : "");
  };

  const handleInput = (e) => {
    setQuery(e.target.value);
    onChange("");          // clear selection on new typing
    setOpen(true);
  };

  const typeColor = (type) =>
    type === "Employee" ? { bg: "#eef2ff", cl: "#4f46e5" } : { bg: "#eff6ff", cl: "#2563eb" };
  const statusColor = (status) =>
    status === "Active"  ? { bg: "#dcfce7", cl: "#16a34a" } :
    status === "Exiting" ? { bg: "#fef2f2", cl: "#ef4444" } :
                           { bg: "#f3f4f6", cl: "#64748b" };

  return (
    <div ref={wrapRef} style={{ position: "relative", flex: 1, minWidth: 220 }}>
      {/* Input box */}
      <div style={{
        display: "flex", alignItems: "center",
        border: open ? "1.5px solid #6366f1" : "1px solid #e2e8f0",
        borderRadius: 8, background: "#fff",
        padding: "0 10px", gap: 6,
        transition: "border-color .15s",
      }}>
        <span style={{ fontSize: 13, color: "#94a3b8" }}>🔍</span>
        <input
          value={selected ? `${selected.name}  (${selected.id})` : query}
          onChange={handleInput}
          onFocus={() => setOpen(true)}
          placeholder="Search & select employee…"
          style={{
            flex: 1, border: "none", outline: "none",
            fontSize: 12, padding: "8px 0",
            color: selected ? "#0f172a" : "#374151",
            fontWeight: selected ? 600 : 400,
            background: "transparent",
          }}
        />
        {/* clear button */}
        {(selected || query) && (
          <button onClick={() => { select(null); setQuery(""); }}
            style={{ border: "none", background: "none", cursor: "pointer",
              color: "#94a3b8", fontSize: 14, padding: "0 2px", lineHeight: 1 }}>
            ✕
          </button>
        )}
        <span style={{ fontSize: 10, color: "#94a3b8", userSelect: "none" }}>▾</span>
      </div>

      {/* Dropdown list */}
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
          background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
          boxShadow: "0 8px 24px rgba(0,0,0,.12)", zIndex: 999,
          maxHeight: 320, overflowY: "auto",
        }}>
          {suggestions.length === 0 ? (
            <div style={{ padding: "16px", textAlign: "center", fontSize: 12, color: "#94a3b8" }}>
              No employees found
            </div>
          ) : (
            <>
              {/* "All" option */}
              <div
                onClick={() => select(null)}
                onMouseEnter={() => setHovered("__all")}
                onMouseLeave={() => setHovered(null)}
                style={{
                  padding: "8px 14px", cursor: "pointer", fontSize: 12,
                  fontWeight: 600, color: "#6366f1",
                  background: hovered === "__all" ? "#f5f3ff" : "#fafafa",
                  borderBottom: "1px solid #e2e8f0",
                }}>
              
              </div>

              {suggestions.map((p) => {
                const tc = typeColor(p.type);
                const sc = statusColor(p.status);
                const isSelected = p.id === selectedId;
                return (
                  <div
                    key={p.id}
                    onClick={() => select(p)}
                    onMouseEnter={() => setHovered(p.id)}
                    onMouseLeave={() => setHovered(null)}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "9px 14px", cursor: "pointer",
                      background: isSelected ? "#f5f3ff" : hovered === p.id ? "#f8fafc" : "#fff",
                      borderBottom: "1px solid #f1f5f9",
                      transition: "background .1s",
                    }}>
                    {/* Avatar initial */}
                    <div style={{
                      width: 30, height: 30, borderRadius: "50%",
                      background: isSelected ? "#6366f1" : "#e0e7ff",
                      color: isSelected ? "#fff" : "#4f46e5",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 12, fontWeight: 700, flexShrink: 0,
                    }}>
                      {(p.name || "?")[0].toUpperCase()}
                    </div>

                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {p.name}
                      </div>
                      <div style={{ fontSize: 10, color: "#64748b", marginTop: 1 }}>
                        {p.id}  ·  {p.dept || "—"}
                      </div>
                    </div>

                    {/* Badges */}
                    <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                      <span style={{ padding: "1px 7px", borderRadius: 10, fontSize: 9,
                        fontWeight: 700, background: tc.bg, color: tc.cl }}>
                        {p.type}
                      </span>
                      <span style={{ padding: "1px 7px", borderRadius: 10, fontSize: 9,
                        fontWeight: 700, background: sc.bg, color: sc.cl }}>
                        {p.status}
                      </span>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Shared select style ─────────────────────────────────────── */
const SEL_STYLE = {
  padding:"7px 12px", border:"1px solid #e2e8f0", borderRadius:8,
  fontSize:12, color:"#374151", background:"#fff",
  cursor:"pointer", outline:"none",
};

/* ── Main Page ───────────────────────────────────────────────── */
function ITAPeoplePg() {
  const [people,  setPeople]  = useState([]);
  const [assets,  setAssets]  = useState([]);
  const [creds,   setCreds]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel,     setSel]     = useState(null);

  /* filter state */
  const [selectedEmpId, setSelectedEmpId] = useState("");
  const [filterDept,    setFilterDept]    = useState("all");
  const [filterType,    setFilterType]    = useState("all");
  const [filterStatus,  setFilterStatus]  = useState("all");
  const [filterMonth,   setFilterMonth]   = useState("all");

  /* load */
  useEffect(() => {
    (async () => {
      try {
        const [pData, aData, cData] = await Promise.all([
          api.getITPeople(), api.getITAssets(), api.getITCredentials(),
        ]);
        setPeople((pData||[]).map((p) => ({
          id:     p.emp_id || p.id,
          numId:  p.numeric_id || p.numericId || p.user_id || "",
          name:   p.name,
          dept:   p.dept || "",
          role:   p.role || "",
          type:   p.type || (p.role==="intern" ? "Intern" : "Employee"),
          status: p.status==="ACTIVE"   ? "Active"
                : p.status==="EXITING"  ? "Exiting"
                : p.status==="INACTIVE" ? "Inactive"
                : p.status || "",
          joined: p.joined || "",
        })));
        setAssets((aData||[]).map((a) => ({
          aid: a.asset_id, assignedTo: a.assigned_to ? String(a.assigned_to) : "",
          empName: a.emp_name||"", assetType: a.asset_type,
          brand: a.brand, serial: a.serial, status: a.status,
        })));
        setCreds((cData||[]).map((c) => ({
          userId: c.user_id ? String(c.user_id) : "", empName: c.emp_name||"",
          lapUser: c.laptop_user||"", ip: c.ip_address||"",
          vpn: !!c.vpn_enabled, email: c.email||"",
          software:  typeof c.software==="string"     ? JSON.parse(c.software||"[]")     : c.software||[],
          ai:        typeof c.ai_tools==="string"     ? JSON.parse(c.ai_tools||"[]")     : c.ai_tools||[],
          sysAccess: typeof c.system_access==="string"? JSON.parse(c.system_access||"[]"): c.system_access||[],
        })));
      } catch (e) { console.warn("Failed to load:", e.message); }
      finally { setLoading(false); }
    })();
  }, []);

  const getAssets = (p) => assets.filter((a) =>
    a.empName===p.name || a.assignedTo===String(p.numId) || a.assignedTo===p.id);
  const getCred = (p) => creds.find((c) =>
    c.empName===p.name || c.userId===String(p.numId) || c.userId===p.id);

  /* dynamic options */
  const depts  = useMemo(() => [...new Set(people.map(p=>p.dept).filter(Boolean))].sort(), [people]);
  const months = useMemo(() => {
    const u = [...new Set(people.map(p=>p.joined).filter(Boolean))];
    return u.sort((a,b) => new Date(a)-new Date(b));
  }, [people]);

  /* filtered list */
  const filtered = useMemo(() => {
    return people
      .map((p) => ({ ...p, _assetCount: getAssets(p).length, _hasCred: !!getCred(p) }))
      .filter((p) => {
        if (selectedEmpId && p.id !== selectedEmpId) return false;
        if (filterDept   !=="all" && p.dept   !==filterDept)   return false;
        if (filterType   !=="all" && p.type   !==filterType)   return false;
        if (filterStatus !=="all" && p.status !==filterStatus) return false;
        if (filterMonth  !=="all" && p.joined !==filterMonth)  return false;
        return true;
      });
  }, [people, assets, creds, selectedEmpId, filterDept, filterType, filterStatus, filterMonth]);

  const anyFilter = !!(selectedEmpId || filterDept!=="all" || filterType!=="all" || filterStatus!=="all" || filterMonth!=="all");

  const resetAll = () => {
    setSelectedEmpId(""); setFilterDept("all"); setFilterType("all");
    setFilterStatus("all"); setFilterMonth("all");
  };

  const csvName = () => {
    const parts = ["people"];
    if (filterMonth  !=="all") parts.push(filterMonth.replace(" ","_"));
    if (filterDept   !=="all") parts.push(filterDept.replace(/\s+/g,"_"));
    if (filterType   !=="all") parts.push(filterType);
    return parts.join("_")+".csv";
  };

  return (
    <div>
      <div className="itapeoplepg-inline-1">Employee IT Profiles</div>

      <div className="card animate-in">

        {/* header */}
        <div className="card-header" style={{flexWrap:"wrap",gap:8}}>
          <div>
            <div className="card-title">All Personnel</div>
            <div className="card-subtitle">
              {anyFilter ? `${filtered.length} of ${people.length} people` : `${people.length} people`}
            </div>
          </div>
          <button
            onClick={() => downloadCSV(filtered, csvName())}
            disabled={loading || filtered.length===0}
            style={{
              display:"flex", alignItems:"center", gap:6,
              padding:"7px 18px", borderRadius:8,
              background: filtered.length===0 ? "#f1f5f9" : "#0f172a",
              color:       filtered.length===0 ? "#94a3b8" : "#fff",
              border:"none", fontSize:12, fontWeight:700,
              cursor: filtered.length===0 ? "not-allowed" : "pointer",
            }}>
            ⬇&nbsp;Download CSV
          </button>
        </div>

        {/* filter toolbar */}
        <div style={{
          padding:"10px 16px", borderBottom:"1px solid #e2e8f0",
          display:"flex", flexWrap:"wrap", gap:8, alignItems:"center",
          background:"#f8fafc",
        }}>

          {/* Employee autocomplete dropdown */}
          <EmpDropdown
            people={people}
            selectedId={selectedEmpId}
            onChange={setSelectedEmpId}
          />

          {/* department */}
          <select value={filterDept} onChange={(e)=>setFilterDept(e.target.value)} style={SEL_STYLE}>
            <option value="all">All Departments</option>
            {depts.map((d)=><option key={d} value={d}>{d}</option>)}
          </select>

          {/* type */}
          <select value={filterType} onChange={(e)=>setFilterType(e.target.value)} style={SEL_STYLE}>
            <option value="all">All Types</option>
            <option value="Employee">Employee</option>
            <option value="Intern">Intern</option>
          </select>

          {/* status */}
          <select value={filterStatus} onChange={(e)=>setFilterStatus(e.target.value)} style={SEL_STYLE}>
            <option value="all">All Status</option>
            <option value="Active">Active</option>
            <option value="Exiting">Exiting</option>
            <option value="Inactive">Inactive</option>
          </select>

          {/* joined month */}
          <select value={filterMonth} onChange={(e)=>setFilterMonth(e.target.value)} style={SEL_STYLE}>
            <option value="all">All Months (Joined)</option>
            {months.map((m)=><option key={m} value={m}>{m}</option>)}
          </select>

          {/* reset */}
          {anyFilter && (
            <button onClick={resetAll} style={{
              padding:"7px 12px", border:"1px solid #fecaca",
              borderRadius:8, background:"#fef2f2", color:"#ef4444",
              fontSize:11, fontWeight:700, cursor:"pointer",
            }}>
              ✕ Reset
            </button>
          )}
        </div>

        {/* table */}
        <div className="card-body itapeoplepg-inline-2">
          {loading ? (
            <div className="itapeoplepg-inline-3">Loading...</div>
          ) : filtered.length===0 ? (
            <div className="itapeoplepg-inline-4">
              {anyFilter ? "No results match your filters." : "No employees found."}
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th><th>Name</th><th>Dept</th><th>Role</th>
                  <th>Type</th><th>Status</th><th>Joined</th>
                  <th>Assets</th><th>Credentials</th><th>Profile</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id}>
                    <td className="itapeoplepg-inline-5">{p.id}</td>
                    <td className="itapeoplepg-inline-6">{p.name}</td>
                    <td>{p.dept}</td>
                    <td>{p.role}</td>
                    <td>
                      <span style={{
                        "--itapeoplepg-1-background": p.type==="Employee" ? "#eef2ff" : "#eff6ff",
                        "--itapeoplepg-1-color":      p.type==="Employee" ? "#4f46e5" : "#2563eb",
                      }} className="itapeoplepg-dyn-1">{p.type}</span>
                    </td>
                    <td>
                      <span style={{
                        "--itapeoplepg-2-background":
                          p.status==="Active"  ? "#eff6ff" :
                          p.status==="Exiting" ? "#fef2f2" : "#f3f4f6",
                        "--itapeoplepg-2-color":
                          p.status==="Active"  ? "#2563eb" :
                          p.status==="Exiting" ? "#ef4444" : "#64748b",
                      }} className="itapeoplepg-dyn-2">{p.status}</span>
                    </td>
                    <td style={{fontSize:11,color:"#64748b"}}>{p.joined||"—"}</td>
                    <td className="itapeoplepg-inline-7">{p._assetCount}</td>
                    <td>
                      <span style={{
                        "--itapeoplepg-3-background": p._hasCred ? "#eff6ff" : "#fef2f2",
                        "--itapeoplepg-3-color":      p._hasCred ? "#2563eb" : "#ef4444",
                      }} className="itapeoplepg-dyn-3">{p._hasCred ? "Yes" : "No"}</span>
                    </td>
                    <td>
                      <button onClick={()=>setSel(p)} className="itapeoplepg-inline-8">
                        👁️ View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Profile Modal */}
      {sel && (() => {
        const pAssets = getAssets(sel);
        const cred    = getCred(sel);
        return (
          <ITAModal open={true} onClose={()=>setSel(null)} title={"IT Profile — "+sel.name}>
            <div className="itapeoplepg-inline-9">
              <div style={{"--itapeoplepg-4-top":"-30px","--itapeoplepg-4-right":"-30px"}} className="itapeoplepg-dyn-4"/>
              <div className="itapeoplepg-inline-10">
                <div className="itapeoplepg-inline-11">{sel.name}</div>
                <div className="itapeoplepg-inline-12">{sel.role} · {sel.dept}</div>
                <div className="itapeoplepg-inline-13">
                  <span className="itapeoplepg-inline-14">{sel.id}</span>
                  <span className="itapeoplepg-inline-15">{sel.type} · {sel.status}</span>
                </div>
                <div className="itapeoplepg-inline-16">Joined: {sel.joined||"—"}</div>
              </div>
            </div>

            <div className="itapeoplepg-inline-17">
              <Sv d={ic.bulk||ic.box} s={16} c="#6366f1"/> Assigned Assets ({pAssets.length})
            </div>
            {pAssets.length===0
              ? <div className="itapeoplepg-inline-18">No assets assigned</div>
              : pAssets.map((a,i)=>(
                <div key={i} className="itapeoplepg-inline-19">
                  <div>
                    <div className="itapeoplepg-inline-20">{a.brand}</div>
                    <div className="itapeoplepg-inline-21">{a.aid} · {a.assetType} · {a.serial}</div>
                  </div>
                  <span style={{
                    "--itapeoplepg-5-background": a.status==="Assigned" ? "#eff6ff" : "#fef3c7",
                    "--itapeoplepg-5-color":      a.status==="Assigned" ? "#2563eb" : "#d97706",
                  }} className="itapeoplepg-dyn-5">{a.status}</span>
                </div>
              ))
            }

            <div className="itapeoplepg-inline-22">
              <div className="itapeoplepg-inline-23">
                <Sv d={ic.shield} s={16} c="#6366f1"/> Credentials & Access
              </div>
              {!cred
                ? <div className="itapeoplepg-inline-24">No credentials provisioned</div>
                : (
                  <div>
                    <div className="itapeoplepg-inline-25">
                      {[["Username",cred.lapUser,true],["IP Address",cred.ip||"—",true],
                        ["Email",cred.email||"—",false],["VPN",cred.vpn?"Enabled":"Disabled",false]
                      ].map(([label,value,mono],i)=>(
                        <div key={i} className="itapeoplepg-inline-26">
                          <div className="itapeoplepg-inline-27">{label}</div>
                          <div style={{"--itapeoplepg-6-font-family":mono?"monospace":"inherit"}} className="itapeoplepg-dyn-6">{value}</div>
                        </div>
                      ))}
                    </div>
                    {(cred.software||[]).length>0&&(
                      <div className="itapeoplepg-inline-28">
                        <span className="itapeoplepg-inline-29">Software</span>
                        <div className="itapeoplepg-inline-30">
                          {cred.software.map((s,i)=><span key={i} className="itapeoplepg-inline-31">{s}</span>)}
                        </div>
                      </div>
                    )}
                    {(cred.ai||[]).length>0&&(
                      <div className="itapeoplepg-inline-32">
                        <span className="itapeoplepg-inline-33">AI Tools</span>
                        <div className="itapeoplepg-inline-34">
                          {cred.ai.map((a,i)=><span key={i} className="itapeoplepg-inline-35">{a}</span>)}
                        </div>
                      </div>
                    )}
                    {(cred.sysAccess||[]).length>0&&(
                      <div>
                        <span className="itapeoplepg-inline-36">System Access</span>
                        <div className="itapeoplepg-inline-37">
                          {cred.sysAccess.map((s,i)=><span key={i} className="itapeoplepg-inline-38">{s}</span>)}
                        </div>
                      </div>
                    )}
                  </div>
                )
              }
            </div>
          {/* Download buttons */}
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end",
              marginTop:20, paddingTop:14, borderTop:"1px solid #e2e8f0" }}>
              <button
                onClick={() => downloadProfileCSV(sel, pAssets, cred)}
                style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 16px",
                  border:"1px solid #e2e8f0", borderRadius:8, background:"#f8fafc",
                  fontSize:12, fontWeight:600, color:"#374151", cursor:"pointer" }}>
                ⬇ Download CSV
              </button>
              <button
                onClick={() => downloadProfilePDF(sel, pAssets, cred)}
                style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 16px",
                  border:"none", borderRadius:8, background:"#1d4ed8",
                  fontSize:12, fontWeight:600, color:"#fff", cursor:"pointer" }}>
                🖨 Download PDF
              </button>
            </div>
          </ITAModal>
        );
      })()}
    </div>
  );
}

export default ITAPeoplePg;
